/**
 * King Living override for useMediaContentSection hook
 * Uses 'full' variant by default to include media (Image/Video) data
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {
  getBaseMediaContentSectionById,
  getBaseMediaContentSections,
  getFullMediaContentSectionById,
  getFullMediaContentSections,
  getMediaContentSectionById,
  getMediaContentSections,
} from '@amblique/contentful/app/contentful/queries';

export function useMediaContentSectionById(id, options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'full',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  const query = variant === 'base' ?
    getBaseMediaContentSectionById :
    variant === 'full' ?
      getFullMediaContentSectionById :
      getMediaContentSectionById;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'mediaContentSection',
      'id',
      id,
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],
    queryFn: async () => {
      const data = await contentfulService?.query(print(query), {
        ...variables,
        id,
        locale: contentfulService.locale,
      }, requestOptions);

      return data?.mediaContentSection || null;
    },
    ...queryOptions,
  });
}

export function useMediaContentSections(options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'full',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  const query = variant === 'base' ?
    getBaseMediaContentSections :
    variant === 'full' ?
      getFullMediaContentSections :
      getMediaContentSections;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'mediaContentSection',
      'collection',
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],
    queryFn: async () => {
      const data = await contentfulService?.query(print(query), {
        ...variables,
        locale: contentfulService.locale,
      }, requestOptions);

      return data?.mediaContentSectionCollection || {total: 0, skip: 0, limit: 0, items: []};
    },
    ...queryOptions,
  });
}
